"use client";

import Link from "next/link";
import React, { useCallback, useEffect, useState } from "react";
import { useEventDetailsPageStore } from "~/state/store";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { slugifyUser, truncate } from "~/utils/string";
import { ConditionalLink, If } from "../Conditional";
import { useSession } from "next-auth/react";
import { Button } from "../themed/Button";
import { toastHandler } from "~/components/ToastHandler";
import { useWhiteLabel, usePermissionsContext } from "~/app/providers";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    useTooltip,
} from "~/components/shadcn/Tooltip";
import { SocialLinksComponent } from "~/components/profile-components/ProfileCard";
import { useIsMobileV2 } from "~/hooks/useIsMobile";
import UserImage from "~/components/users/UserImage";
import Linkify from "linkify-react";
import {
    DragDropContext,
    Draggable,
    Droppable,
    DropResult,
} from "@hello-pangea/dnd";
import { updateHostOrder } from "~/app/events/[slug]/actions";
import { LuGripVertical } from "react-icons/lu";
import { toast } from "react-toastify";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { TooltipPortal } from "@radix-ui/react-tooltip";

type HostTooltipInputProps = {
    user: {
        id: string;
        name: string | null;
        image?: string | null;
        bio?: string | null;
        slug?: string | null;
        socialLinks?: PrismaJson.SocialLinks;
        organizationName?: string | null;
        useOrganizationIdentity?: boolean | null;
        organizationImage?: string | null;
    };
    children: React.ReactNode;
    triggerClassName?: string;
    align?: "start" | "center" | "end";
    side?: "top" | "right" | "bottom" | "left";
};

export const HostTooltip = ({
    user,
    children,
    triggerClassName,
    align = "center",
    side = "top",
}: HostTooltipInputProps) => {
    const { showTooltip, handleTooltipClick, setShowTooltip } = useTooltip();
    const customization = useWhiteLabel()?.customization;

    return (
        <TooltipProvider delayDuration={0}>
            <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
                <TooltipTrigger
                    onClick={handleTooltipClick}
                    className={triggerClassName}
                >
                    {children}
                </TooltipTrigger>
                <TooltipPortal>
                    <TooltipContent
                        className={cn(
                            customization?.mainCardClassName || "bg-black/97",
                            "flex max-w-[300px] gap-4",
                        )}
                        align={align}
                        side={side}
                    >
                        <UserAvatar user={user} className="shrink-0" />
                        <div>
                            <div
                                className={cn(
                                    "flex flex-col gap-2",
                                    customization?.labelClassName,
                                )}
                            >
                                <span>
                                    {user.useOrganizationIdentity &&
                                    user.organizationName
                                        ? user.organizationName
                                        : user.name}
                                </span>
                                {user.socialLinks && (
                                    <SocialLinksComponent
                                        className={cn(
                                            "h-3 w-3",
                                            customization?.labelClassName,
                                        )}
                                        socialLinks={user.socialLinks}
                                    />
                                )}
                            </div>
                            <p
                                style={{ wordBreak: "break-word" }}
                                className={cn(
                                    "line-clamp-3 whitespace-normal break-words pt-2 text-sm",
                                    customization?.descriptionClassName,
                                )}
                            >
                                <Linkify
                                    options={{
                                        className: cn(
                                            "text-secondary",
                                            customization?.linkClassName,
                                        ),
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        truncate: 128,
                                    }}
                                >
                                    {truncate(user.bio ?? "", 128)}
                                </Linkify>
                            </p>
                            <div className="my-2 justify-self-end">
                                <Link
                                    className={cn(
                                        "underline",
                                        customization?.linkClassName ||
                                            "hover:text-v3-primary",
                                    )}
                                    href={`/profile/${slugifyUser(user)}`}
                                >
                                    View profile
                                </Link>
                            </div>
                        </div>
                    </TooltipContent>
                </TooltipPortal>
            </Tooltip>
        </TooltipProvider>
    );
};

export const HostTooltipClickable = ({
    children,
    triggerClassName,
    ...props
}: HostTooltipInputProps) => {
    const isMobile = useIsMobileV2();

    return (
        <HostTooltip
            {...props}
            triggerClassName={isMobile ? triggerClassName : undefined}
        >
            <ConditionalLink
                isLink={!isMobile}
                href={`/profile/${slugifyUser(props.user)}`}
                className={triggerClassName}
            >
                {children}
            </ConditionalLink>
        </HostTooltip>
    );
};

type Props = {
    hosts: EventBySlugOutput["hosts"];
    eventId: string;
    eventSlug: string;
    onHostAdd?: () => void;
};

const HostedBy = ({ hosts, eventId, eventSlug, onHostAdd }: Props) => {
    const { panelMode } = useEventDetailsPageStore();
    const { data: session } = useSession();
    const isHost = !!hosts.find((h) => h.user.id === session?.user.id);
    const [isMobile, setIsMobile] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [orderedHosts, setOrderedHosts] = useState(hosts);
    const permissions = usePermissionsContext();
    const hasManagingPerms = !!permissions?.isAdmin || !!permissions?.isManager;

    useEffect(() => {
        setIsMobile(useIsMobileV2());
    }, []);

    useEffect(() => {
        // Sort hosts by order when they change
        setOrderedHosts(
            [...hosts].sort((a, b) => (a.order || 0) - (b.order || 0)),
        );
    }, [hosts]);

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = async (result: DropResult) => {
        setIsDragging(false);

        if (!result.destination || !hasManagingPerms) return;

        const newOrderedHosts = [...orderedHosts];
        const [reorderedHost] = newOrderedHosts.splice(result.source.index, 1);
        newOrderedHosts.splice(result.destination.index, 0, reorderedHost!);

        // update local state immediately, optimistic update
        setOrderedHosts(newOrderedHosts);

        // Update order in database using server action
        try {
            const result = await updateHostOrder({
                eventSlug,
                hostOrders: newOrderedHosts.map((host, index) => ({
                    hostId: host.id,
                    order: index,
                })),
            });

            if (!result.success) {
                console.error("Failed to update host order:", result.error);
                // Revert to original order on error
                toast.error("Failed to update host order");
                setOrderedHosts(hosts);
            }
        } catch (error) {
            console.error("Failed to update host order:", error);
            toast.error("Failed to update host order");
            // Revert to original order on error
            setOrderedHosts(hosts);
        }
    };

    if (panelMode === "default" && !hosts.length) return false;

    return (
        <div className="flex flex-row items-center gap-2">
            <If condition={hosts?.length === 0}>
                <div className="flex w-full flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <p className="grow text-v3-N-300">Host Needed</p>
                        <BecomeHostButton
                            isHost={isHost}
                            eventId={eventId}
                            onHostAdd={onHostAdd}
                        />
                    </div>
                </div>
            </If>

            <If condition={hosts?.length !== 0}>
                <div className="flex w-full flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <p className="grow text-sm text-v3-N-300">Hosted By</p>
                        <BecomeHostButton
                            isHost={isHost}
                            eventId={eventId}
                            onHostAdd={onHostAdd}
                        />
                    </div>

                    <DragDropContext
                        onDragEnd={handleDragEnd}
                        onDragStart={handleDragStart}
                    >
                        <Droppable droppableId="orderedHosts">
                            {(provided) => (
                                <div
                                    className="flex flex-col gap-1 rounded-md bg-v3-N-500 p-2"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {orderedHosts.map((host, index) => (
                                        <Draggable
                                            key={host.id}
                                            draggableId={host.id}
                                            index={index}
                                            isDragDisabled={
                                                !hasManagingPerms ||
                                                orderedHosts.length <= 1
                                            }
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="group flex w-full items-center"
                                                >
                                                    <div className="flex w-full items-center gap-4 rounded-md p-1 hover:bg-v3-N-600">
                                                        <div className="flex grow items-center gap-4">
                                                            <HostTooltip
                                                                user={host.user}
                                                                triggerClassName="flex items-center gap-4 grow"
                                                            >
                                                                <ConditionalLink
                                                                    href={`/profile/${slugifyUser(host.user)}`}
                                                                >
                                                                    <UserImage
                                                                        user={
                                                                            host.user
                                                                        }
                                                                        iconSize={
                                                                            38
                                                                        }
                                                                    />
                                                                </ConditionalLink>

                                                                <ConditionalLink
                                                                    isLink={
                                                                        !isMobile
                                                                    }
                                                                    href={`/profile/${slugifyUser(host.user)}`}
                                                                    className={cn(
                                                                        `transition-[color] duration-300`,
                                                                        `hover:text-secondary`,
                                                                        `cursor-pointer`,
                                                                    )}
                                                                >
                                                                    <div className="flex flex-col">
                                                                        <div>
                                                                            {
                                                                                host
                                                                                    .user
                                                                                    .name
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </ConditionalLink>
                                                            </HostTooltip>
                                                        </div>
                                                        {hasManagingPerms &&
                                                            orderedHosts.length >
                                                                1 && (
                                                                <div
                                                                    className={cn(
                                                                        "ml-auto cursor-grab px-1",
                                                                        !isMobile &&
                                                                            "opacity-0 group-hover:opacity-100",
                                                                    )}
                                                                >
                                                                    <LuGripVertical className="h-4 w-4 text-v3-N-300" />
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </If>
        </div>
    );
};

const BecomeHostButton = ({
    isHost,
    eventId,
    onHostAdd,
}: {
    isHost: boolean;
    eventId: string;
    onHostAdd?: () => void;
}) => {
    const { panelMode } = useEventDetailsPageStore();
    const { data: session } = useSession();

    const permissions = usePermissionsContext();
    const hasManagingPerms = !!permissions?.isHost || !!permissions?.isManager;
    const addHost = api.event.addHost.useMutation();

    const handleBecomeHost = useCallback(async () => {
        if (!session) throw new Error("user not logged in");

        await toastHandler({
            loadingMessage: "Becoming a host...",
            successMessage: "Assigned as host successfully",
            errorMessage: "Failed to become a host",
            operation: async () => {
                await addHost.mutateAsync({ eventId, userId: session.user.id });
                onHostAdd && onHostAdd();
            },
        });
    }, [session]);

    return (
        <If condition={panelMode === "edit" && hasManagingPerms && !isHost}>
            <Button
                onClick={handleBecomeHost}
                variant="secondary"
                size="xs"
                className="text-xs"
            >
                Become a host
            </Button>
        </If>
    );
};

export default HostedBy;
